import { rem } from "@mantine/core";
import classNames from "classnames";
import {
  MRT_TopToolbar,
  type MRT_RowData,
  type MRT_TableOptions
} from "mantine-react-table-open";
import { MRT_Localization_DE } from "mantine-react-table-open/locales/de/index.esm.mjs";
import { NoDataMessage } from "../components/BuildingBlocks/NoDataMessage/NoDataMessage";
import { MRTActiveFiltersRow } from "./components/MRTActiveFiltersRow/MRTActiveFiltersRow";
import { ToggleFullscreenButton } from "./components/ToggleFullscreenButton/ToggleFullscreenButton";
import { TruncatedHeader } from "./TruncatedHeader";

export interface DefaultMRTOptionsOptions<TData extends MRT_RowData> {
  emptyRowsFallbackText?: string;
  searchTextInput?: {
    placeholder?: string;
  };
  initialState?: Partial<MRT_TableOptions<TData>["initialState"]>;
  className?: string;
  "aria-label"?: string;
  hideBorder?: boolean;
  /** Opt in to table scrolling within the modal's remaining height. */
  fitModalHeight?: boolean;
}

/** Define re-useable default Mantine-React-Table options */
export function getDefaultMRTOptions<TData extends MRT_RowData>(
  options: DefaultMRTOptionsOptions<TData> = {}
): Partial<MRT_TableOptions<TData>> {
  return {
    ...getDefaultMRTStyling<TData>(options),
    defaultColumn: {
      Header: ({ column }) => (
        <TruncatedHeader>{column.columnDef.header}</TruncatedHeader>
      )
    },
    enableColumnActions: false,
    enableTopToolbar: true,
    enableToolbarInternalActions: true,
    initialState: {
      density: "xs",
      pagination: {
        pageIndex: 0,
        pageSize: 10
      },
      columnSizing: { "mrt-row-expand": 40 },
      showToolbarDropZone: false,
      ...options.initialState
    },
    localization: MRT_Localization_DE,
    mantineTableBodyCellProps: ({ table, row, column }) => {
      const isEditMode =
        table.getState().editingRow?.id === row.id ||
        table.getState().creatingRow !== null;

      const columnCanEdit = column.columnDef.enableEditing !== false;
      if (isEditMode && columnCanEdit) {
        // Align content to the top so inputs aren't weirdly aligned when some have an error message and some don't
        return { style: { alignContent: "start" } };
      } else return {};
    },
    paginationDisplayMode: "pages",
    mantinePaginationProps: { hideWithOnePage: true },
    positionToolbarDropZone: "none",
    positionActionsColumn: "last",
    positionGlobalFilter: "left",
    enableStickyHeader: true,
    getRowId: (row) => row.id,
    renderEmptyRowsFallback: () => (
      <NoDataMessage
        className="mrt-no-data-component"
        message={options.emptyRowsFallbackText}
      />
    ),
    renderTopToolbar: ({ table }) => {
      return (
        <>
          <MRT_TopToolbar table={table} />
          <MRTActiveFiltersRow table={table} />
        </>
      );
    },
    renderToolbarInternalActions: ({ table }) => (
      <ToggleFullscreenButton table={table} />
    )
  };
}

function getDefaultMRTStyling<TData extends MRT_RowData>(
  options: DefaultMRTOptionsOptions<TData> = {}
): Partial<MRT_TableOptions<TData>> {
  return {
    /* container */
    mantinePaperProps: {
      ...(options.hideBorder && {
        p: 0,
        withBorder: false,
        shadow: "none"
      }),
      className: classNames(options.className, {
        "mrt-fit-modal-height": options.fitModalHeight
      })
    },

    /* table */
    mantineTableProps: {
      "aria-label": options["aria-label"] ?? "Tabelle",
      verticalSpacing: "xs",
      style: {
        /* Collapsed borders on the sticky header scroll away with the table.
        Row borders are drawn on the cells instead (mantineReactTable.scss). */
        borderCollapse: "separate",
        borderSpacing: 0
      }
    },
    mantineTableHeadRowProps: {
      fz: "sm"
    },
    mantineTableHeadCellProps: {
      py: "sm"
    },
    mantineTableFooterCellProps: {
      py: "xs",
      fz: "sm"
    },

    /* toolbar */
    mantineTopToolbarProps: {
      className: "mrt-top-toolbar",
      py: "md"
    },
    mantineSearchTextInputProps: {
      miw: rem(410),
      m: 0,
      placeholder: options.searchTextInput?.placeholder ?? "Suche...",
      variant: "default"
    },

    /* table components */
    mantineColumnActionsButtonProps: {
      size: "xs"
    },
    mantineSelectAllCheckboxProps: {
      size: "sm",
      color: "white"
    },
    mantineSelectCheckboxProps: {
      size: "sm"
    }
  };
}
